// The Rollout aside: the draft run over the last 30 days' voice users
// (POST /policy/preview), and "Who gets what" for one user through the draft.

import { useState } from "react";
import type { ClientPlatform, DryRun, LookupRequest, Preview } from "../../services/voiceEngines";
import { DryRunResult } from "./parts";
import { armColor, engineLabel, sourceText } from "./labels";
import { parseWho } from "./testUsers";

// Sources decided before the bucket step, in ladder order. No keyboard:
// Keyboard Mode is per call, so the preview resolves spoken calls.
const BEFORE_BUCKET = ["kill_switch", "override", "override_env", "policy_off", "client_build"];

const REASON_TEXT: Record<string, string> = {
  voice_engine_not_allowed: "engine not allowed here → cascade",
  voice_engine_profile_missing: "profile missing → built-in default",
};

export function DistributionPanel({
  preview,
  previewing,
  error,
  blocked,
}: {
  preview: Preview | null;
  previewing: boolean;
  error: string | null;
  blocked: boolean;
}) {
  const dist = preview?.distribution ?? null;
  return (
    <section className="panel" aria-labelledby="ve-prev-h" aria-busy={previewing}>
      <h2 id="ve-prev-h" className="panel-head">
        Preview · last {dist?.days ?? 30} days
        {previewing && <span className="muted ve-small">updating…</span>}
      </h2>
      {blocked ? (
        <p className="muted ve-small">Fix the draft to see who it would reach.</p>
      ) : error ? (
        <p className="error-text">Preview unavailable ({error}).</p>
      ) : !dist ? (
        <p className="muted ve-small">{preview && !preview.valid ? "The server rejected this draft." : "Loading…"}</p>
      ) : (
        <>
          <p className="muted ve-small" style={{ marginTop: 0 }}>
            The {dist.users.toLocaleString()} users who made a spoken app call since{" "}
            {new Date(dist.since).toLocaleDateString(undefined, { month: "short", day: "numeric" })}, run through this
            draft.
            {dist.truncated && " Only the first users were read; the counts are a sample."}
          </p>
          {dist.arms.map((a, i) => (
            <div key={a.key} className="ve-list-row">
              <span className="ve-dot" style={{ background: armColor(i) }} aria-hidden="true" />
              <span className="mono" style={{ fontWeight: 600 }}>
                {a.key}
              </span>
              <span className="muted ve-small grow">
                {engineLabel(a.engine)} · {a.profile ?? "built-in"}
                {a.served < a.users && ` · ${(a.users - a.served).toLocaleString()} get the cascade (not allowed)`}
              </span>
              <span className="ve-num">{a.users.toLocaleString()}</span>
            </div>
          ))}
          <div className="ve-list-row">
            <span className="ve-dot rest" aria-hidden="true" />
            <span className="mono" style={{ fontWeight: 600 }}>
              rest
            </span>
            <span className="muted ve-small grow">Cascade, bucket outside every arm</span>
            <span className="ve-num">{dist.rest.toLocaleString()}</span>
          </div>
          <div className="ve-sub">Decided before the bucket</div>
          <div className="chip-row">
            {BEFORE_BUCKET.map((s) => (
              <span key={s} className="chip" title={sourceText(s)}>
                <span className="mono">{s}</span> {(dist.bySource[s] ?? 0).toLocaleString()}
              </span>
            ))}
          </div>
          <div className="ve-sub">Engine they would run</div>
          <div className="chip-row">
            {Object.entries(dist.byEngine).map(([e, n]) => (
              <span key={e} className="chip">
                {engineLabel(e)} {n.toLocaleString()}
              </span>
            ))}
          </div>
          {Object.values(dist.reasons).some((n) => n > 0) && (
            <>
              <div className="ve-sub">Downgraded</div>
              <div className="chip-row">
                {Object.entries(dist.reasons)
                  .filter(([, n]) => n > 0)
                  .map(([r, n]) => (
                    <span key={r} className="chip chip-bench">
                      {REASON_TEXT[r] ?? r} {n.toLocaleString()}
                    </span>
                  ))}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

const CLIENTS: { value: "" | ClientPlatform; label: string }[] = [
  { value: "", label: "Their latest app" },
  { value: "ios", label: "iOS" },
  { value: "android", label: "Android" },
  { value: "legacy", label: "Legacy client" },
];

export function LookupPanel({
  result,
  lookup,
  error,
  blocked,
  onLookup,
}: {
  // The lookup's answer, only when it is for `lookup` (not a previous person's).
  result: DryRun | null;
  lookup: LookupRequest | null;
  error: string | null;
  blocked: boolean;
  onLookup: (lookup: LookupRequest | null) => void;
}) {
  const [who, setWho] = useState("");
  const [platform, setPlatform] = useState<"" | ClientPlatform>("");
  const [build, setBuild] = useState("");
  const [keyboard, setKeyboard] = useState(false);

  const run = () => {
    const target = parseWho(who);
    if (!target) return onLookup(null);
    const req: LookupRequest = { ...target, keyboard };
    if (platform) req.platform = platform;
    if (platform && platform !== "legacy" && /^\d+$/.test(build.trim())) req.build = Number(build.trim());
    onLookup(req);
  };

  return (
    <section className="panel" aria-labelledby="ve-look-h">
      <h2 id="ve-look-h" className="panel-head">
        Who gets what
      </h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run();
        }}
      >
        <label htmlFor="ve-look">Email or uid</label>
        <div className="ve-inline">
          <input id="ve-look" type="search" value={who} onChange={(e) => setWho(e.target.value)} />
          <button type="submit" className="btn">
            Look up
          </button>
        </div>
        <div className="ve-fields">
          <div>
            <label htmlFor="ve-look-client">As</label>
            <select
              id="ve-look-client"
              value={platform}
              onChange={(e) => setPlatform(e.target.value as "" | ClientPlatform)}
            >
              {CLIENTS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          {(platform === "ios" || platform === "android") && (
            <div>
              <label htmlFor="ve-look-build">Build</label>
              <input
                id="ve-look-build"
                type="number"
                min={0}
                value={build}
                onChange={(e) => setBuild(e.target.value)}
              />
            </div>
          )}
        </div>
        <label className="ve-check">
          <input type="checkbox" checked={keyboard} onChange={(e) => setKeyboard(e.target.checked)} />
          <span>In Keyboard Mode</span>
        </label>
      </form>
      <div className="ve-help">Runs through this draft, not the live value.</div>
      {error && <p className="error-text">{error}</p>}
      {lookup && blocked && <p className="muted ve-small">Fix the draft to look someone up through it.</p>}
      {lookup && !blocked && !result && !error && <p className="muted ve-small">Looking up…</p>}
      {result && <DryRunResult run={result} who={lookup?.email} />}
    </section>
  );
}
